import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import { LOGO_URL } from '@/lib/brand';
import { toast } from 'sonner';
import api from '@/lib/api';

const sportOptions = ['Football', 'Cricket', 'Badminton', 'Swimming', 'Tennis', 'Basketball', 'Hockey', 'Athletics', 'Table Tennis', 'Other'];

// Map server-side spam/validation error codes → user-readable text.
const ERROR_MESSAGES = {
  academy_name_too_short: 'Academy name must be at least 3 characters.',
  academy_name_repeating_chars: 'Academy name looks invalid — please re-type it without repeated characters.',
  academy_name_keysmash_no_vowels: 'Academy name looks invalid — please use a proper name.',
  academy_name_already_exists: 'An academy with this name already exists. Try adding a city or unique suffix.',
  owner_name_too_short: 'Please enter your full name.',
  owner_name_repeating_chars: 'Your name looks invalid — please re-type it.',
  owner_name_keysmash_no_vowels: 'Your name looks invalid — please use your real name.',
  email_invalid_format: 'Please enter a valid email address.',
  email_disposable_domain: 'Please use your work email — disposable inboxes are not supported.',
  email_already_registered: 'This email is already registered. Try signing in instead.',
  phone_too_short: 'Phone number is too short. Include country code, e.g. +91…',
};

export default function Signup() {
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', academy_name: '', sport_types: [], city: '',
  });
  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.academy_name) {
      toast.error('Please fill in your name, email and academy name.');
      return;
    }
    setLoading(true);
    try {
      // Phase 323 — Email Verification Gate. No DB rows are created
      // until the user clicks the magic link in their inbox.
      const res = await api.post('/auth/initiate-signup', {
        academy_name: form.academy_name,
        owner_name: form.name,
        email: form.email.trim().toLowerCase(),
        phone: form.phone,
        sport_types: form.sport_types.length > 0 ? form.sport_types : undefined,
        city: form.city || undefined,
      });
      if (res.data?.ok) {
        setSentTo(res.data.email || form.email);
        toast.success('Check your inbox to verify and launch your workspace.');
      } else {
        toast.error('Something went wrong — please try again.');
      }
    } catch (err) {
      const code = err?.response?.data?.detail?.error;
      const msg = ERROR_MESSAGES[code] || err?.response?.data?.detail?.error
        || err?.response?.data?.detail || 'Signup failed. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Signup failed.');
    } finally {
      setLoading(false);
    }
  };

  // ── "Check your inbox" success screen ─────────────────────────────
  if (sentTo) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8" data-testid="signup-inbox-screen">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(254,252,232,0.85)_0%,rgba(248,250,252,0.92)_42%,rgba(248,250,252,1)_70%)]" />
        <div className="relative w-full max-w-md">
          <Card className="border shadow-lg">
            <CardContent className="text-center py-10 px-8">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mb-5">
                <Mail className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-display font-bold mb-2">Check your inbox</h2>
              <p className="text-sm text-muted-foreground mb-5">
                We sent a magic link to <span className="font-semibold text-foreground">{sentTo}</span>.
                Click it to verify your email and launch your Out-Play workspace.
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-left text-xs text-slate-600 mb-5">
                <div className="flex items-start gap-2 mb-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>The link expires in 1 hour. You can close this tab.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Don&apos;t see the email? Check your spam folder.</span>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => { setSentTo(null); setForm({ ...form, email: '' }); }}
                data-testid="signup-resend-link"
                className="w-full"
              >
                Use a different email
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Already verified?{' '}
                <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Signup form ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8" data-testid="signup-page">
      <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(254,252,232,0.85)_0%,rgba(248,250,252,0.92)_42%,rgba(248,250,252,1)_70%)]" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 text-sm transition-colors duration-150">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src={LOGO_URL} alt="Out-Play" className="h-10 w-10 rounded-lg object-contain" />
            <span className="font-display font-bold text-2xl">Out-Play</span>
          </div>
        </div>
        <Card className="border shadow-lg">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl font-display">Create Your Academy</CardTitle>
            <CardDescription>Start your 15-day free trial — verify by email, no card needed.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-xs">Your Name *</Label>
                  <Input id="name" placeholder="Full name" value={form.name} onChange={(e) => updateForm('name', e.target.value)} data-testid="signup-name-input" autoFocus />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-xs">WhatsApp Number</Label>
                  <Input id="phone" placeholder="+91 98765..." value={form.phone} onChange={(e) => updateForm('phone', e.target.value)} data-testid="signup-phone-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs">Work Email *</Label>
                <Input id="email" type="email" placeholder="you@academy.com" value={form.email} onChange={(e) => updateForm('email', e.target.value)} data-testid="signup-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="academy_name" className="text-xs">Academy Name *</Label>
                <Input id="academy_name" placeholder="e.g., Champions Football Academy" value={form.academy_name} onChange={(e) => updateForm('academy_name', e.target.value)} data-testid="signup-academy-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Primary Sport</Label>
                  <Select onValueChange={(v) => updateForm('sport_types', [v])}>
                    <SelectTrigger data-testid="signup-sport-select">
                      <SelectValue placeholder="Select sport" />
                    </SelectTrigger>
                    <SelectContent>
                      {sportOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="city" className="text-xs">City</Label>
                  <Input id="city" placeholder="e.g., Bangalore" value={form.city} onChange={(e) => updateForm('city', e.target.value)} data-testid="signup-city-input" />
                </div>
              </div>
              <Button type="submit" className="w-full mt-2" disabled={loading} data-testid="auth-signup-submit-button">
                {loading ? 'Sending magic link...' : 'Send Magic Link to Verify Email'}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center pt-1">
                You&apos;ll set your password after verifying your email.
              </p>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-4">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="signup-login-link">Sign in</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
